import React, { useState } from 'react';
import { Box, TextField, IconButton, InputAdornment, CircularProgress, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { generateSummary, improveComment } from '../../../../../api/openai/openaiAPI';
import RichTextEditor from 'components/RichTextEditor';
import {
	CommentFormState,
	CommentFormStateErrors,
} from '../../hooks/useCommentEditorForm';

interface RichActivityTextProps {
	formState: CommentFormState;
	handleChange: <T extends keyof CommentFormState>(
		field: T,
		value: CommentFormState[T],
		validate?: boolean,
	) => void;
	errors: CommentFormStateErrors;
}

const RichActivityText: React.FC<RichActivityTextProps> = (props) => {
	const { formState, handleChange, errors } = props;
	const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
	const [isImprovingComment, setIsImprovingComment] = useState(false);

	const handleAutoFillSummary = async () => {
		if (!formState.description || formState.description.trim().length < 10) {
			handleChange('title', 'this is a summary');
			return;
		}

		setIsGeneratingSummary(true);

		try {
			const summary = await generateSummary(formState.description);
			handleChange('title', summary);
		} catch (error) {
			console.error('Error generating AI summary:', error);
			handleChange('title', 'Error generating summary - please try again');
		} finally {
			setIsGeneratingSummary(false);
		}
	};

	const handleImproveComment = async () => {
		if (!formState.description || formState.description.trim().length < 10) {
			return;
		}

		setIsImprovingComment(true);

		try {
			const improvedComment = await improveComment(formState.description);
			handleChange('description', improvedComment);
		} catch (error) {
			console.error('Error improving comment:', error);
			console.warn('Failed to improve comment - please try again');
		} finally {
			setIsImprovingComment(false);
		}
	};

	const isAISummaryEnabled = () => {
		return formState.description && formState.description.trim().length >= 10;
	};

	const isAICommentImprovementEnabled = () => {
		return formState.description && formState.description.trim().length >= 10;
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
			<TextField
				value={formState.title}
				onChange={(e) => handleChange('title', e.target.value)}
				label="Summary"
				variant="outlined"
				fullWidth
				multiline
				error={!!errors.title}
				helperText={errors.title}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								onClick={handleAutoFillSummary}
								edge="end"
								aria-label="generate AI summary"
								disabled={isGeneratingSummary || !isAISummaryEnabled()}
								title={
									!formState.description || formState.description.trim().length === 0
										? "Add content to the comment field to generate an AI summary"
										: formState.description.trim().length < 10
										? `Add at least ${10 - formState.description.trim().length} more characters to generate AI summary`
										: "Generate AI summary from comment"
								}
								sx={{
									opacity: isAISummaryEnabled() ? 1 : 0.3,
									transition: 'opacity 0.2s ease-in-out'
								}}
							>
								{isGeneratingSummary ? (
									<CircularProgress size={20} />
								) : (
									<AutoAwesomeIcon />
								)}
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
			<Box sx={{ position: 'relative' }}>
				<RichTextEditor
					value={formState.description}
					onChange={(content: string) => handleChange('description', content)}
					height={300}
					placeholder="Enter your comment..."
				/>
				<Tooltip
					title={
						!isAICommentImprovementEnabled()
							? 'Add at least 10 characters to improve with AI'
							: 'Improve comment with AI'
					}
				>
					<Box
						component="span"
						sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
					>
						<IconButton
							onClick={handleImproveComment}
							aria-label="improve comment with AI"
							disabled={isImprovingComment || !isAICommentImprovementEnabled()}
							size="small"
							sx={{
								bgcolor: 'background.paper',
								boxShadow: 1,
								opacity: isAICommentImprovementEnabled() ? 1 : 0.3,
								transition: 'opacity 0.2s ease-in-out',
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							{isImprovingComment ? (
								<CircularProgress size={18} />
							) : (
								<AutoAwesomeIcon fontSize="small" />
							)}
						</IconButton>
					</Box>
				</Tooltip>
			</Box>
		</Box>
	);
};

export default RichActivityText;
